/**
 * Dual-support force-distribution control (Gate A push 16).
 *
 * Pure TypeScript — no box3d / React / Three. Computes force-consistent leg
 * joint torques for forward dual-contact (place|L1R1 → load) only.
 *
 * Pipeline:
 *   1. Desired body wrench = gravity compensation + COM PD (ω=√(g/h)) + upright
 *   2. Split feet by measured-load share ramp (0.45 balanced law) + friction cone
 *   3. Per-foot force → joint torques via planar Jacobian-transpose (2-link + ankle)
 *   4. Motor delivery: velocity in torque direction, maxTorqueNm = |τ|
 *      (+ low-gain knee support spring so the column cannot buckle)
 */

import {
  CAPTURE_FOOT_DROP_M,
  CAPTURE_GRAVITY_MPS2,
  CAPTURE_MIN_COM_HEIGHT_M,
  CAPTURE_SHANK_LENGTH_M,
  CAPTURE_THIGH_LENGTH_M,
} from './humanoidMuscleCapture';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Spec friction 0.6 with margin — never demand shear beyond this cone. */
export const FORCE_DIST_MU = 0.6 * 0.85;

/** Balanced new-foot load fraction at which share / authority is full (matches P1A). */
export const FORCE_DIST_LOAD_SHARE_FULL = 0.45;

/** Default body mass (kg) when observation/spec does not provide one. */
export const FORCE_DIST_DEFAULT_MASS_KG = 75;

/** COM PD damping ratio (ζ) — under-critically damped for soft plant. */
export const FORCE_DIST_COM_ZETA = 0.7;

/** Horizontal force bound as multiple of body weight. */
export const FORCE_DIST_HORIZ_FORCE_BW = 0.55;

/** Upright torso torque bound (N·m). */
export const FORCE_DIST_UPRIGHT_TORQUE_MAX_NM = 120;

/** Upright gain: N·m per unit tilt of pelvis up-vector horizontal component. */
export const FORCE_DIST_UPRIGHT_KP = 280;

/** Upright angular-velocity damp (N·m per rad/s). */
export const FORCE_DIST_UPRIGHT_KD = 28;

/**
 * Position-error seed (rad) used so the velocity servo saturates in the torque
 * direction: target = measured + sign(τ) · seed. Large enough that KP·error
 * hits the motor speed ceiling; torque is clamped by maxTorqueNm = |τ|.
 */
export const FORCE_DIST_TORQUE_ERR_RAD = 0.35;

/** Soft knee support floor (rad, negative = flex). Parallel spring only. */
export const FORCE_DIST_KNEE_FLOOR_RAD = -0.14;

/** Low-gain parallel spring on the knee floor (N·m / rad). */
export const FORCE_DIST_KNEE_SPRING_NM_PER_RAD = 80;

/** Knee spring torque cap (N·m). */
export const FORCE_DIST_KNEE_SPRING_MAX_NM = 80;

/**
 * Min-support posture targets (controller convention). Parallel springs pull
 * toward these so the kinematic chain can carry gravity when J is near-singular
 * at full extension (pure Jᵀ·Fy → 0 on a straight leg).
 */
export const FORCE_DIST_SUPPORT_HIP_SAG_RAD = 0.08;
export const FORCE_DIST_SUPPORT_KNEE_RAD = -0.1;
export const FORCE_DIST_SUPPORT_ANKLE_SAG_RAD = -0.02;

/** Posture spring gains (N·m/rad) — low-gain parallel, not stiff plant servo. */
export const FORCE_DIST_POSTURE_HIP_KP = 160;
export const FORCE_DIST_POSTURE_KNEE_KP = 120;
export const FORCE_DIST_POSTURE_ANKLE_KP = 40;
export const FORCE_DIST_POSTURE_HIP_LAT_KP = 90;

/** Virtual knee flex (rad) injected into gravity Jᵀ so stand is not singular. */
export const FORCE_DIST_GRAVITY_VIRTUAL_KNEE_RAD = -0.18;

/** Torque budget caps so motors stay within the existing capacity table. */
export const FORCE_DIST_HIP_TORQUE_MAX_NM = 1000;
export const FORCE_DIST_KNEE_TORQUE_MAX_NM = 250;
export const FORCE_DIST_ANKLE_TORQUE_MAX_NM = 70;
export const FORCE_DIST_SPINE_TORQUE_MAX_NM = 700;

/** Physics fixed-step (s) used to convert normal impulse → force when dt absent. */
export const FORCE_DIST_DEFAULT_DT_S = 1 / 60;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ForceDistVec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Body wrench in world frame (force N, torque N·m about COM). */
export interface BodyWrench {
  readonly fx: number;
  readonly fy: number;
  readonly fz: number;
  readonly tx: number;
  readonly ty: number;
  readonly tz: number;
}

/** Per-foot desired contact force (ground on foot), world frame. */
export interface FootForce {
  readonly fx: number;
  readonly fy: number;
  readonly fz: number;
}

/** Planar joint configuration (controller convention). */
export interface LegJointConfig {
  readonly hipSagRad: number;
  readonly hipLatRad: number;
  readonly kneeRad: number;
  readonly ankleSagRad: number;
  readonly ankleLatRad: number;
}

/** Per-leg joint torques (controller convention, N·m). */
export interface LegJointTorques {
  readonly hipSagNm: number;
  readonly hipLatNm: number;
  readonly kneeNm: number;
  readonly ankleSagNm: number;
  readonly ankleLatNm: number;
}

export interface ForceDistMotorJoint {
  readonly targetOffsetRad: number;
  readonly targetLatRad: number;
  readonly maxTorqueNm: number;
}

export interface ForceDistMotorCommands {
  readonly leftHip: ForceDistMotorJoint;
  readonly rightHip: ForceDistMotorJoint;
  readonly leftKnee: ForceDistMotorJoint;
  readonly rightKnee: ForceDistMotorJoint;
  readonly leftAnkle: ForceDistMotorJoint;
  readonly rightAnkle: ForceDistMotorJoint;
  readonly spine: ForceDistMotorJoint;
  /** Diagnostic / tests. */
  readonly bodyWrench: BodyWrench;
  readonly leftForce: FootForce;
  readonly rightForce: FootForce;
  readonly shareNew: number;
  readonly authority01: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function finiteOr(v: number | undefined, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export function forceDistThighLengthM(): number {
  return CAPTURE_THIGH_LENGTH_M;
}

export function forceDistShankLengthM(): number {
  return CAPTURE_SHANK_LENGTH_M + CAPTURE_FOOT_DROP_M;
}

// ---------------------------------------------------------------------------
// 1. Desired body wrench
// ---------------------------------------------------------------------------

/**
 * Desired dual-support body wrench each tick.
 *
 * - Vertical: gravity compensation (mg up through the feet)
 * - Horizontal: PD pulling COM toward support-polygon center
 *   with ω = √(g/h), kp = m·ω², kd = 2·ζ·m·ω (not magic numbers)
 * - Torso: bounded upright torque from pelvis up-vector + angular velocity
 */
export function computeDesiredBodyWrench(args: {
  massKg?: number;
  com: ForceDistVec3;
  comVel: ForceDistVec3;
  supportCenter: ForceDistVec3;
  /** Unit pelvis local-up in world. */
  pelvisUp?: ForceDistVec3;
  pelvisAngularVel?: ForceDistVec3;
  gravityMps2?: number;
  zeta?: number;
}): BodyWrench {
  const mass = Math.max(1, finiteOr(args.massKg, FORCE_DIST_DEFAULT_MASS_KG));
  const g = Math.max(1e-6, finiteOr(args.gravityMps2, CAPTURE_GRAVITY_MPS2));
  const zeta = clamp(finiteOr(args.zeta, FORCE_DIST_COM_ZETA), 0.2, 1.5);
  const h = Math.max(CAPTURE_MIN_COM_HEIGHT_M, args.com.y - 0.03);
  const omega = Math.sqrt(g / h);
  const kp = mass * omega * omega;
  const kd = 2 * zeta * mass * omega;
  const fBound = FORCE_DIST_HORIZ_FORCE_BW * mass * g;

  const errX = args.com.x - args.supportCenter.x;
  const errZ = args.com.z - args.supportCenter.z;
  const fx = clamp(-kp * errX - kd * args.comVel.x, -fBound, fBound);
  const fz = clamp(-kp * errZ - kd * args.comVel.z, -fBound, fBound);
  const fy = mass * g;

  const up = args.pelvisUp ?? { x: 0, y: 1, z: 0 };
  const w = args.pelvisAngularVel ?? { x: 0, y: 0, z: 0 };
  // Tilt proxies: horizontal components of up-vector.
  // tx rights sagittal (about +X): counteract up.z
  // tz rights lateral (about +Z): counteract -up.x
  const tx = clamp(
    -FORCE_DIST_UPRIGHT_KP * up.z - FORCE_DIST_UPRIGHT_KD * w.x,
    -FORCE_DIST_UPRIGHT_TORQUE_MAX_NM,
    FORCE_DIST_UPRIGHT_TORQUE_MAX_NM,
  );
  const tz = clamp(
    FORCE_DIST_UPRIGHT_KP * up.x - FORCE_DIST_UPRIGHT_KD * w.z,
    -FORCE_DIST_UPRIGHT_TORQUE_MAX_NM,
    FORCE_DIST_UPRIGHT_TORQUE_MAX_NM,
  );

  return { fx, fy, fz, tx, ty: 0, tz };
}

// ---------------------------------------------------------------------------
// 2. Load-share ramp + friction cone
// ---------------------------------------------------------------------------

/**
 * Desired new-foot load-share authority ∈ [0, 1].
 * authority = clamp(newFootLoadFraction / 0.45, 0, 1)
 * Monotone non-decreasing in measured new-foot load fraction.
 */
export function forceDistLoadShareAuthority(
  newFootLoadFraction: number,
  loadFull: number = FORCE_DIST_LOAD_SHARE_FULL,
): number {
  if (!Number.isFinite(newFootLoadFraction) || loadFull <= 0) return 0;
  return clamp(newFootLoadFraction / loadFull, 0, 1);
}

/**
 * Front (new) foot share of the body wrench.
 * Rises from 0 → 0.5 as authority 0 → 1 (balanced at the 0.45 threshold).
 */
export function forceDistNewFootShare(authority01: number): number {
  const a = clamp(Number.isFinite(authority01) ? authority01 : 0, 0, 1);
  return 0.5 * a;
}

/**
 * Cap horizontal force to the friction cone |Fh| ≤ μ · N.
 * Returns the capped (fx, fz) and whether the cone was active.
 */
export function forceDistCapFrictionCone(
  fx: number,
  fz: number,
  normalN: number,
  mu: number = FORCE_DIST_MU,
): { fx: number; fz: number; capped: boolean; maxHoriz: number } {
  const n = Math.max(0, Number.isFinite(normalN) ? normalN : 0);
  const m = Math.max(0, Number.isFinite(mu) ? mu : 0);
  const maxHoriz = m * n;
  const h = Math.hypot(fx, fz);
  if (h <= maxHoriz + 1e-12 || h < 1e-12) {
    return { fx, fz, capped: false, maxHoriz };
  }
  const s = maxHoriz / h;
  return { fx: fx * s, fz: fz * s, capped: true, maxHoriz };
}

/**
 * Split body wrench into per-foot GRFs.
 *
 * Vertical and horizontal use the same share schedule. Horizontal components
 * are friction-cone capped by μ · measured normal (never demand shear a
 * lightly-loaded foot cannot supply). Residual horizontal after capping the
 * new foot is reassigned to the stance foot within its cone.
 */
export function splitWrenchToFeet(args: {
  wrench: BodyWrench;
  /** New (front) foot is left when true. */
  leftIsNew: boolean;
  newFootLoadFraction: number;
  /** Measured normal force (N) per foot — impulse/dt or weight proxy. */
  leftNormalN: number;
  rightNormalN: number;
  mu?: number;
  loadFull?: number;
}): {
  left: FootForce;
  right: FootForce;
  shareNew: number;
  authority01: number;
} {
  const authority01 = forceDistLoadShareAuthority(
    args.newFootLoadFraction,
    args.loadFull,
  );
  const shareNew = forceDistNewFootShare(authority01);
  const shareStance = 1 - shareNew;
  const mu = args.mu ?? FORCE_DIST_MU;

  const w = args.wrench;
  const newFy = shareNew * w.fy;
  const stanceFy = shareStance * w.fy;
  let newFx = shareNew * w.fx;
  let newFz = shareNew * w.fz;
  let stanceFx = shareStance * w.fx;
  let stanceFz = shareStance * w.fz;

  // Couple a portion of horizontal torso yaw-free upright into feet as pure force
  // (no free moment about vertical). Lateral/sagittal torques are carried as
  // differential vertical is small — we approximate via horizontal redistribution
  // already in the COM PD. Spine command handles residual upright.

  const leftN = Math.max(0, args.leftNormalN);
  const rightN = Math.max(0, args.rightNormalN);
  const newN = args.leftIsNew ? leftN : rightN;
  const stanceN = args.leftIsNew ? rightN : leftN;

  const newCap = forceDistCapFrictionCone(newFx, newFz, newN, mu);
  // Residual horizontal reassigned to stance.
  const residualFx = newFx - newCap.fx;
  const residualFz = newFz - newCap.fz;
  newFx = newCap.fx;
  newFz = newCap.fz;
  stanceFx += residualFx;
  stanceFz += residualFz;
  const stanceCap = forceDistCapFrictionCone(stanceFx, stanceFz, stanceN, mu);
  stanceFx = stanceCap.fx;
  stanceFz = stanceCap.fz;

  const newForce: FootForce = { fx: newFx, fy: newFy, fz: newFz };
  const stanceForce: FootForce = { fx: stanceFx, fy: stanceFy, fz: stanceFz };

  return {
    left: args.leftIsNew ? newForce : stanceForce,
    right: args.leftIsNew ? stanceForce : newForce,
    shareNew,
    authority01,
  };
}

// ---------------------------------------------------------------------------
// 3. Jacobian-transpose: per-foot force → joint torques
// ---------------------------------------------------------------------------

/**
 * Sagittal-plane foot position relative to hip (controller convention).
 *
 * ```text
 * r_z = −L1·sin(θh) − L2·sin(θh+θk)
 * r_y = −L1·cos(θh) − L2·cos(θh+θk)
 * ```
 *
 * Matches stand (θh=θk=0 → foot at (0,−(L1+L2))) and FK sag− → +Z
 * (θh < 0 → r_z > 0).
 */
export function forceDistSagittalFootOffset(
  hipSagRad: number,
  kneeRad: number,
  thighLenM: number = CAPTURE_THIGH_LENGTH_M,
  shankLenM: number = CAPTURE_SHANK_LENGTH_M + CAPTURE_FOOT_DROP_M,
): { z: number; y: number } {
  const th = hipSagRad;
  const tk = hipSagRad + kneeRad;
  return {
    z: -thighLenM * Math.sin(th) - shankLenM * Math.sin(tk),
    y: -thighLenM * Math.cos(th) - shankLenM * Math.cos(tk),
  };
}

/**
 * Planar sagittal Jacobian J (2×2) mapping [ω_hip, ω_knee] → [v_z, v_y].
 * Rows: z, y. Columns: hipSag, knee.
 */
export function forceDistSagittalJacobian(
  hipSagRad: number,
  kneeRad: number,
  thighLenM: number = CAPTURE_THIGH_LENGTH_M,
  shankLenM: number = CAPTURE_SHANK_LENGTH_M + CAPTURE_FOOT_DROP_M,
): readonly [readonly [number, number], readonly [number, number]] {
  const th = hipSagRad;
  const tk = hipSagRad + kneeRad;
  // ∂rz/∂θh, ∂rz/∂θk
  const jzh = -thighLenM * Math.cos(th) - shankLenM * Math.cos(tk);
  const jzk = -shankLenM * Math.cos(tk);
  // ∂ry/∂θh, ∂ry/∂θk
  const jyh = thighLenM * Math.sin(th) + shankLenM * Math.sin(tk);
  const jyk = shankLenM * Math.sin(tk);
  return [
    [jzh, jzk],
    [jyh, jyk],
  ];
}

/**
 * Lateral Jacobian (scalar lever): hip_lat rate → lateral foot velocity ≈
 * |r_y| · ω_lat (small-angle). Ankle lat uses a short lever (foot half-width proxy).
 */
export function forceDistLateralLevers(
  hipSagRad: number,
  kneeRad: number,
  thighLenM: number = CAPTURE_THIGH_LENGTH_M,
  shankLenM: number = CAPTURE_SHANK_LENGTH_M + CAPTURE_FOOT_DROP_M,
): { hipLatLeverM: number; ankleLatLeverM: number } {
  const off = forceDistSagittalFootOffset(hipSagRad, kneeRad, thighLenM, shankLenM);
  const hipLatLeverM = Math.max(0.15, Math.abs(off.y));
  const ankleLatLeverM = 0.05;
  return { hipLatLeverM, ankleLatLeverM };
}

/**
 * Ankle sagittal lever (short): τ_ankle_sag ≈ −L_foot · F_z contribution via
 * CAPTURE_FOOT_DROP_M as moment arm for shear/vertical couple.
 */
export function forceDistAnkleSagTorqueNm(
  footForce: FootForce,
  ankleSagRad: number,
  footLeverM: number = CAPTURE_FOOT_DROP_M,
): number {
  // Plantarflex (+) resists forward shear (−Fz on foot from ground is rare);
  // ground-on-foot +Fz (forward) → ankle plantarflex positive for balance.
  // τ = lever × horizontal sagittal + small vertical×sin(ankle).
  return footLeverM * footForce.fz + 0.02 * footForce.fy * Math.sin(ankleSagRad);
}

/**
 * Map a single foot's desired GRF to leg joint torques via Jᵀ F.
 *
 * Sagittal: [τ_h, τ_k] = Jᵀ [F_z, F_y]
 * Lateral:  τ_hip_lat = lever · F_x; τ_ankle_lat = ankleLever · F_x
 * Ankle sag: short lever on F_z
 *
 * Signs: positive τ in controller convention (extend / abduct toward +X).
 */
export function footForceToLegTorques(
  footForce: FootForce,
  config: LegJointConfig,
  opts?: {
    thighLenM?: number;
    shankLenM?: number;
  },
): LegJointTorques {
  const L1 = opts?.thighLenM ?? CAPTURE_THIGH_LENGTH_M;
  const L2 = opts?.shankLenM ?? CAPTURE_SHANK_LENGTH_M + CAPTURE_FOOT_DROP_M;
  const J = forceDistSagittalJacobian(
    config.hipSagRad,
    config.kneeRad,
    L1,
    L2,
  );
  // τ = Jᵀ F_sag with F_sag = [Fz, Fy]
  const hipSagNm = J[0][0] * footForce.fz + J[1][0] * footForce.fy;
  const kneeNm = J[0][1] * footForce.fz + J[1][1] * footForce.fy;
  const ankleSagNm = forceDistAnkleSagTorqueNm(footForce, config.ankleSagRad);

  const levers = forceDistLateralLevers(
    config.hipSagRad,
    config.kneeRad,
    L1,
    L2,
  );
  // Hip lat+: abduct toward +X. Ground-on-foot +Fx → hip lat torque sign + for left;
  // use unified sign (caller may mirror if needed — both legs same world Fx).
  const hipLatNm = levers.hipLatLeverM * footForce.fx;
  const ankleLatNm = levers.ankleLatLeverM * footForce.fx;

  return {
    hipSagNm,
    hipLatNm,
    kneeNm,
    ankleSagNm,
    ankleLatNm,
  };
}

/**
 * Low-gain parallel knee spring toward a minimum support posture.
 * Additive to Jacobian torque; not a stiff position servo.
 */
export function forceDistKneeSupportSpringNm(
  kneeRad: number,
  floorRad: number = FORCE_DIST_KNEE_FLOOR_RAD,
  kNmPerRad: number = FORCE_DIST_KNEE_SPRING_NM_PER_RAD,
  maxNm: number = FORCE_DIST_KNEE_SPRING_MAX_NM,
): number {
  if (!Number.isFinite(kneeRad)) return 0;
  if (kneeRad >= floorRad) return 0;
  // knee more flexed than floor → positive (extend) torque
  return clamp(kNmPerRad * (floorRad - kneeRad), 0, maxNm);
}

/**
 * Parallel posture springs toward a min-support configuration.
 * Provides the structural column torques that pure Jᵀ misses near full extension.
 */
export function forceDistPostureSpringTorques(config: LegJointConfig): LegJointTorques {
  const hipSagNm =
    FORCE_DIST_POSTURE_HIP_KP *
    (FORCE_DIST_SUPPORT_HIP_SAG_RAD - config.hipSagRad);
  const kneeNm =
    FORCE_DIST_POSTURE_KNEE_KP *
    (FORCE_DIST_SUPPORT_KNEE_RAD - config.kneeRad);
  const ankleSagNm =
    FORCE_DIST_POSTURE_ANKLE_KP *
    (FORCE_DIST_SUPPORT_ANKLE_SAG_RAD - config.ankleSagRad);
  const hipLatNm = FORCE_DIST_POSTURE_HIP_LAT_KP * (0 - config.hipLatRad);
  const ankleLatNm = FORCE_DIST_POSTURE_ANKLE_KP * (0 - config.ankleLatRad);
  return {
    hipSagNm,
    hipLatNm,
    kneeNm,
    ankleSagNm,
    ankleLatNm,
  };
}

/**
 * Gravity-support torques via Jᵀ with a virtual knee bend so the column is not
 * singular at stand. Uses the desired vertical foot force only.
 */
export function forceDistGravitySupportTorques(
  verticalForceN: number,
  config: LegJointConfig,
  opts?: { thighLenM?: number; shankLenM?: number },
): Pick<LegJointTorques, 'hipSagNm' | 'kneeNm'> {
  const kneeVirt = Math.min(config.kneeRad, FORCE_DIST_GRAVITY_VIRTUAL_KNEE_RAD);
  const tau = footForceToLegTorques(
    { fx: 0, fy: Math.max(0, verticalForceN), fz: 0 },
    { ...config, kneeRad: kneeVirt },
    opts,
  );
  return { hipSagNm: tau.hipSagNm, kneeNm: tau.kneeNm };
}

// ---------------------------------------------------------------------------
// 4. Torque → motor command (saturated velocity servo ≈ torque source)
// ---------------------------------------------------------------------------

/**
 * Convert desired joint torque into a motor command.
 *
 * Measured on this velocity-servo bridge (push 16):
 * - Pure "velocity in τ direction" collapses the column (J near-singular for
 *   vertical load; joints wander).
 * - Structural support is a **plant position hold**; force distribution owns
 *   the **torque budget** (friction-aware, load-share ramped).
 *
 * ```text
 * target      = plantRad  (min-support / plant pose)
 * maxTorqueNm = clamp(max(|τ_force|, supportFloor), …, cap)
 * ```
 *
 * Optional small bias (biasScale>0) nudges the servo in the τ direction for
 * shear correction without abandoning the plant.
 */
export function forceDistTorqueToMotor(
  measuredRad: number,
  torqueNm: number,
  torqueCapNm: number,
  errSeedRad: number = FORCE_DIST_TORQUE_ERR_RAD,
  plantRad?: number,
  opts?: {
    supportFloorNm?: number;
    biasScale?: number;
  },
): { targetRad: number; maxTorqueNm: number } {
  const tau = Number.isFinite(torqueNm) ? torqueNm : 0;
  const cap = Math.max(0, torqueCapNm);
  const absTau = Math.min(cap, Math.abs(tau));
  const measured = Number.isFinite(measuredRad) ? measuredRad : 0;
  const plant =
    typeof plantRad === 'number' && Number.isFinite(plantRad)
      ? plantRad
      : measured;
  const floor = Math.min(
    cap,
    Math.max(0, opts?.supportFloorNm ?? 0),
  );
  const maxTorqueNm = Math.max(floor, absTau, 1e-3);
  const biasScale = clamp(opts?.biasScale ?? 0, 0, 1);
  if (biasScale <= 0 || absTau < 1e-3) {
    return { targetRad: plant, maxTorqueNm };
  }
  const sign = tau >= 0 ? 1 : -1;
  const bias =
    biasScale * clamp(absTau / Math.max(cap, 1e-6), 0, 1);
  return {
    targetRad: plant + sign * bias * Math.abs(errSeedRad),
    maxTorqueNm,
  };
}

/**
 * Base support torque floors (N·m) so the column can hold weight through the
 * 2 s load window. Aligned with plant-path budgets
 * (baseMaxTorque × ~1.55 tScale): hips ~1550, knees ~390, ankles ~115.
 */
export const FORCE_DIST_HIP_SUPPORT_FLOOR_NM = 1400;
export const FORCE_DIST_KNEE_SUPPORT_FLOOR_NM = 350;
export const FORCE_DIST_ANKLE_SUPPORT_FLOOR_NM = 100;
export const FORCE_DIST_SPINE_SUPPORT_FLOOR_NM = 900;

// ---------------------------------------------------------------------------
// Full dual-support solver
// ---------------------------------------------------------------------------

export interface ForceDistSolveArgs {
  massKg?: number;
  com: ForceDistVec3;
  comVel: ForceDistVec3;
  supportCenter: ForceDistVec3;
  pelvisUp?: ForceDistVec3;
  pelvisAngularVel?: ForceDistVec3;
  /** New (front / previous-swing) foot is left when true. */
  leftIsNew: boolean;
  newFootLoadFraction: number;
  leftNormalN: number;
  rightNormalN: number;
  leftConfig: LegJointConfig;
  rightConfig: LegJointConfig;
  mu?: number;
  dtS?: number;
}

/**
 * Full dual-support force distribution → motor-ready joint commands.
 */
export function solveDualSupportForceDistribution(
  args: ForceDistSolveArgs,
): ForceDistMotorCommands {
  const wrench = computeDesiredBodyWrench({
    massKg: args.massKg,
    com: args.com,
    comVel: args.comVel,
    supportCenter: args.supportCenter,
    pelvisUp: args.pelvisUp,
    pelvisAngularVel: args.pelvisAngularVel,
  });

  const split = splitWrenchToFeet({
    wrench,
    leftIsNew: args.leftIsNew,
    newFootLoadFraction: args.newFootLoadFraction,
    leftNormalN: args.leftNormalN,
    rightNormalN: args.rightNormalN,
    mu: args.mu,
  });

  // Shear / horizontal map at live config (vertical Fy stripped — gravity path
  // below owns weight support with a non-singular virtual knee).
  const leftForceMap = footForceToLegTorques(
    { fx: split.left.fx, fy: 0, fz: split.left.fz },
    args.leftConfig,
  );
  const rightForceMap = footForceToLegTorques(
    { fx: split.right.fx, fy: 0, fz: split.right.fz },
    args.rightConfig,
  );

  // Gravity column: Jᵀ with virtual knee so stand is not singular.
  const leftGrav = forceDistGravitySupportTorques(
    split.left.fy,
    args.leftConfig,
  );
  const rightGrav = forceDistGravitySupportTorques(
    split.right.fy,
    args.rightConfig,
  );

  // Parallel posture springs (min-support) + hard knee floor spring.
  const leftPosture = forceDistPostureSpringTorques(args.leftConfig);
  const rightPosture = forceDistPostureSpringTorques(args.rightConfig);
  const leftKneeFloor = forceDistKneeSupportSpringNm(args.leftConfig.kneeRad);
  const rightKneeFloor = forceDistKneeSupportSpringNm(args.rightConfig.kneeRad);

  // Total torque: force map (shear-dominant at live J) + gravity column + posture.
  // Vertical pure-Fy at live stand is near-zero in leftForceMap — gravity path fills it.
  const leftHipSagNm =
    leftForceMap.hipSagNm + leftGrav.hipSagNm + leftPosture.hipSagNm;
  const rightHipSagNm =
    rightForceMap.hipSagNm + rightGrav.hipSagNm + rightPosture.hipSagNm;
  const leftKneeTotal =
    leftForceMap.kneeNm +
    leftGrav.kneeNm +
    leftPosture.kneeNm +
    leftKneeFloor;
  const rightKneeTotal =
    rightForceMap.kneeNm +
    rightGrav.kneeNm +
    rightPosture.kneeNm +
    rightKneeFloor;
  const leftHipLatNm = leftForceMap.hipLatNm + leftPosture.hipLatNm;
  const rightHipLatNm = rightForceMap.hipLatNm + rightPosture.hipLatNm;
  const leftAnkSagNm = leftForceMap.ankleSagNm + leftPosture.ankleSagNm;
  const rightAnkSagNm = rightForceMap.ankleSagNm + rightPosture.ankleSagNm;
  const leftAnkLatNm = leftForceMap.ankleLatNm + leftPosture.ankleLatNm;
  const rightAnkLatNm = rightForceMap.ankleLatNm + rightPosture.ankleLatNm;

  // Stance gets full support floors; new foot ramps floors with authority so a
  // lightly-loaded sole cannot inject large plant wrench (friction-consistent).
  const auth = split.authority01;
  // New-leg floor ramps with measured-load authority (friction-aware share).
  // Floor 0.4 so early plant still holds structure; 1.0 at balanced 0.45 load.
  const newFloorScale = 0.4 + 0.6 * auth;
  const leftIsNew = args.leftIsNew;
  const leftHipFloor =
    FORCE_DIST_HIP_SUPPORT_FLOOR_NM * (leftIsNew ? newFloorScale : 1);
  const rightHipFloor =
    FORCE_DIST_HIP_SUPPORT_FLOOR_NM * (leftIsNew ? 1 : newFloorScale);
  const leftKneeFloorNm =
    FORCE_DIST_KNEE_SUPPORT_FLOOR_NM * (leftIsNew ? newFloorScale : 1);
  const rightKneeFloorNm =
    FORCE_DIST_KNEE_SUPPORT_FLOOR_NM * (leftIsNew ? 1 : newFloorScale);
  const leftAnkFloor =
    FORCE_DIST_ANKLE_SUPPORT_FLOOR_NM * (leftIsNew ? newFloorScale : 1);
  const rightAnkFloor =
    FORCE_DIST_ANKLE_SUPPORT_FLOOR_NM * (leftIsNew ? 1 : newFloorScale);

  // Shear bias only on horizontal-mapped joints (small); plant holds structure.
  const shearBias = 0.2;

  const lHipSag = forceDistTorqueToMotor(
    args.leftConfig.hipSagRad,
    leftHipSagNm,
    FORCE_DIST_HIP_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    FORCE_DIST_SUPPORT_HIP_SAG_RAD,
    { supportFloorNm: leftHipFloor, biasScale: shearBias },
  );
  const lHipLat = forceDistTorqueToMotor(
    args.leftConfig.hipLatRad,
    leftHipLatNm,
    FORCE_DIST_HIP_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    0,
    { supportFloorNm: leftHipFloor * 0.5, biasScale: shearBias },
  );
  const rHipSag = forceDistTorqueToMotor(
    args.rightConfig.hipSagRad,
    rightHipSagNm,
    FORCE_DIST_HIP_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    FORCE_DIST_SUPPORT_HIP_SAG_RAD,
    { supportFloorNm: rightHipFloor, biasScale: shearBias },
  );
  const rHipLat = forceDistTorqueToMotor(
    args.rightConfig.hipLatRad,
    rightHipLatNm,
    FORCE_DIST_HIP_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    0,
    { supportFloorNm: rightHipFloor * 0.5, biasScale: shearBias },
  );
  const lKnee = forceDistTorqueToMotor(
    args.leftConfig.kneeRad,
    leftKneeTotal,
    FORCE_DIST_KNEE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    FORCE_DIST_SUPPORT_KNEE_RAD,
    { supportFloorNm: leftKneeFloorNm, biasScale: 0.1 },
  );
  const rKnee = forceDistTorqueToMotor(
    args.rightConfig.kneeRad,
    rightKneeTotal,
    FORCE_DIST_KNEE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    FORCE_DIST_SUPPORT_KNEE_RAD,
    { supportFloorNm: rightKneeFloorNm, biasScale: 0.1 },
  );
  const lAnkSag = forceDistTorqueToMotor(
    args.leftConfig.ankleSagRad,
    leftAnkSagNm,
    FORCE_DIST_ANKLE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    FORCE_DIST_SUPPORT_ANKLE_SAG_RAD,
    { supportFloorNm: leftAnkFloor, biasScale: shearBias },
  );
  const lAnkLat = forceDistTorqueToMotor(
    args.leftConfig.ankleLatRad,
    leftAnkLatNm,
    FORCE_DIST_ANKLE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    0,
    { supportFloorNm: leftAnkFloor * 0.5, biasScale: shearBias },
  );
  const rAnkSag = forceDistTorqueToMotor(
    args.rightConfig.ankleSagRad,
    rightAnkSagNm,
    FORCE_DIST_ANKLE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    FORCE_DIST_SUPPORT_ANKLE_SAG_RAD,
    { supportFloorNm: rightAnkFloor, biasScale: shearBias },
  );
  const rAnkLat = forceDistTorqueToMotor(
    args.rightConfig.ankleLatRad,
    rightAnkLatNm,
    FORCE_DIST_ANKLE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    0,
    { supportFloorNm: rightAnkFloor * 0.5, biasScale: shearBias },
  );

  // Spine: upright plant 0 + torque-budgeted motors from wrench.
  const spineSag = forceDistTorqueToMotor(
    0,
    wrench.tx,
    FORCE_DIST_SPINE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    0,
    { supportFloorNm: FORCE_DIST_SPINE_SUPPORT_FLOOR_NM, biasScale: 0.15 },
  );
  const spineLat = forceDistTorqueToMotor(
    0,
    wrench.tz,
    FORCE_DIST_SPINE_TORQUE_MAX_NM,
    FORCE_DIST_TORQUE_ERR_RAD,
    0,
    { supportFloorNm: FORCE_DIST_SPINE_SUPPORT_FLOOR_NM * 0.5, biasScale: 0.15 },
  );

  // Hip spherical: combine sag target with lat; torque budget = max of components
  // (bridge uses one maxTorque for the spherical motor).
  const leftHipTorque = Math.min(
    FORCE_DIST_HIP_TORQUE_MAX_NM,
    Math.hypot(lHipSag.maxTorqueNm, lHipLat.maxTorqueNm),
  );
  const rightHipTorque = Math.min(
    FORCE_DIST_HIP_TORQUE_MAX_NM,
    Math.hypot(rHipSag.maxTorqueNm, rHipLat.maxTorqueNm),
  );
  const leftAnkleTorque = Math.min(
    FORCE_DIST_ANKLE_TORQUE_MAX_NM,
    Math.hypot(lAnkSag.maxTorqueNm, lAnkLat.maxTorqueNm),
  );
  const rightAnkleTorque = Math.min(
    FORCE_DIST_ANKLE_TORQUE_MAX_NM,
    Math.hypot(rAnkSag.maxTorqueNm, rAnkLat.maxTorqueNm),
  );

  return {
    leftHip: {
      targetOffsetRad: lHipSag.targetRad,
      targetLatRad: lHipLat.targetRad,
      maxTorqueNm: leftHipTorque,
    },
    rightHip: {
      targetOffsetRad: rHipSag.targetRad,
      targetLatRad: rHipLat.targetRad,
      maxTorqueNm: rightHipTorque,
    },
    leftKnee: {
      targetOffsetRad: lKnee.targetRad,
      targetLatRad: 0,
      maxTorqueNm: lKnee.maxTorqueNm,
    },
    rightKnee: {
      targetOffsetRad: rKnee.targetRad,
      targetLatRad: 0,
      maxTorqueNm: rKnee.maxTorqueNm,
    },
    leftAnkle: {
      targetOffsetRad: lAnkSag.targetRad,
      targetLatRad: lAnkLat.targetRad,
      maxTorqueNm: leftAnkleTorque,
    },
    rightAnkle: {
      targetOffsetRad: rAnkSag.targetRad,
      targetLatRad: rAnkLat.targetRad,
      maxTorqueNm: rightAnkleTorque,
    },
    spine: {
      targetOffsetRad: spineSag.targetRad,
      targetLatRad: spineLat.targetRad,
      maxTorqueNm: Math.min(
        FORCE_DIST_SPINE_TORQUE_MAX_NM,
        Math.hypot(spineSag.maxTorqueNm, spineLat.maxTorqueNm),
      ),
    },
    bodyWrench: wrench,
    leftForce: split.left,
    rightForce: split.right,
    shareNew: split.shareNew,
    authority01: split.authority01,
  };
}

/**
 * Gate: force-distribution owns forward dual-contact only.
 * Zero-forward / non-dual / non-forward → false (P1A identity path).
 */
export function forceDistDualSupportActive(args: {
  commandedStepForwardM: number;
  stepPhase: 'none' | 'prepare' | 'lift' | 'hold' | 'place' | 'load';
  bothFeetContact: boolean;
}): boolean {
  if (
    typeof args.commandedStepForwardM !== 'number' ||
    !Number.isFinite(args.commandedStepForwardM) ||
    args.commandedStepForwardM <= 1e-4
  ) {
    return false;
  }
  if (args.stepPhase !== 'place' && args.stepPhase !== 'load') return false;
  return args.bothFeetContact === true;
}

/**
 * Estimate measured normal force (N) from post-solver impulse and load share.
 * Prefer impulse/dt; fall back to fraction · mg.
 */
export function forceDistNormalForceN(args: {
  normalImpulse?: number;
  loadFraction?: number;
  massKg?: number;
  dtS?: number;
  gravityMps2?: number;
}): number {
  const dt = Math.max(1e-4, finiteOr(args.dtS, FORCE_DIST_DEFAULT_DT_S));
  const imp = args.normalImpulse;
  if (typeof imp === 'number' && Number.isFinite(imp) && imp >= 0) {
    return imp / dt;
  }
  const mass = Math.max(1, finiteOr(args.massKg, FORCE_DIST_DEFAULT_MASS_KG));
  const g = finiteOr(args.gravityMps2, CAPTURE_GRAVITY_MPS2);
  const frac = clamp(finiteOr(args.loadFraction, 0), 0, 1);
  return frac * mass * g;
}
